import { motion } from 'framer-motion';
import { useState } from 'react';
import PageTransition from '../components/common/PageTransition';
import SampleCard from '../components/samples/SampleCard';
import PDFViewer from '../components/samples/PDFViewer';
import { staggerContainer, staggerItem } from '../utils/animations';
import { samples as initialSamples } from '../data/samples';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { FaWhatsapp, FaEye } from 'react-icons/fa';
import SEO from '../components/common/SEO';

const howToUse = [
  { icon: '👀', title: 'Browse Samples', description: 'Click on any sample card to view the full PDF preview right in your browser.', color: '#7B2FBE' },
  { icon: '📱', title: 'Read & Explore', description: 'Navigate through the pages to see stories, puzzles, and activities.', color: '#1565C0' },
  { icon: '💝', title: 'Subscribe', description: 'Love what you see? Subscribe to get new issues every week!', color: '#3CB044' },
];

const benefits = [
  { icon: '📅', title: 'Weekly Issues', description: 'Fresh content delivered every single week', color: '#1565C0' },
  { icon: '🎨', title: 'High Quality', description: 'Professional design & expertly crafted content', color: '#7B2FBE' },
  { icon: '🏆', title: 'Win Prizes', description: 'Monthly contests with exciting rewards', color: '#F57C00' },
  { icon: '📚', title: '2 Editions', description: 'Tailored content for ages 4–9 and 10–15', color: '#3CB044' },
];

const Samples = () => {
  const [samples] = useLocalStorage('tinytimes-samples', initialSamples);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [filterEdition, setFilterEdition] = useState('all');

  const filteredSamples = filterEdition === 'all'
    ? samples
    : samples.filter(s => s.edition === filterEdition);

  return (
    <PageTransition>
      <SEO
        title="Free Sample Issues - Read Online"
        description="Flip through and read free sample issues of The Tiny Times. Experience our beautifully illustrated stories, fun quizzes, and brain-teasing puzzles."
        keywords="free kids magazine sample, read online kids stories, sample childrens puzzles, free weekly activities"
      />
      <div className="min-h-screen">

        {/* Hero */}
        <section className="relative py-24 md:py-36 overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #EFF6FF 0%, #F3EEFF 40%, #E8F5E9 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(21,101,192,0.12) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute bottom-0 left-0 w-80 h-80 opacity-15 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(123,47,190,0.3), transparent 70%)', filter: 'blur(50px)', transform: 'translate(-25%,25%)' }} />

          {[{ e: '📄', x: '7%', y: '22%' }, { e: '📚', x: '86%', y: '18%' }, { e: '✨', x: '79%', y: '70%' }, { e: '🌈', x: '11%', y: '70%' }].map((item, i) => (
            <motion.div key={i} className="absolute text-5xl opacity-20 pointer-events-none select-none"
              style={{ left: item.x, top: item.y }}
              animate={{ y: [0, -20, 0], rotate: [0, 12, -12, 0] }}
              transition={{ duration: 4 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}>
              {item.e}
            </motion.div>
          ))}

          <div className="container mx-auto px-4 relative z-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                className="text-8xl mb-5">📖</motion.div>
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-5 leading-tight" style={{ color: '#1A1F2E' }}>
                Free{' '}
                <span style={{ background: 'linear-gradient(135deg, #1565C0, #7B2FBE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Samples
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-gray-dark leading-relaxed max-w-xl mx-auto">
                Explore our latest editions for free! See exactly what makes The Tiny Times special 🌟
              </p>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path fill="white" d="M0,40L60,45C120,50,240,60,360,55C480,50,600,30,720,28C840,25,960,35,1080,40C1200,45,1320,42,1380,40L1440,38L1440,80L0,80Z" />
            </svg>
          </div>
        </section>

        {/* Filter Tabs */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { id: 'all', icon: '📚', label: 'All Samples' },
                { id: 'micro', icon: '🌈', label: 'Micro Edition (4–9)' },
                { id: 'macro', icon: '🚀', label: 'Macro Edition (10–15)' },
              ].map(({ id, icon, label }) => {
                const isActive = filterEdition === id;
                return (
                  <motion.button key={id} onClick={() => setFilterEdition(id)}
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-button font-display font-semibold text-sm transition-all"
                    style={isActive
                      ? { background: '#7B2FBE', color: 'white', boxShadow: '0 4px 14px rgba(123,47,190,0.3)' }
                      : { background: 'white', color: '#7B2FBE', border: '1.5px solid rgba(123,47,190,0.25)' }
                    }>
                    <span className="text-xl">{icon}</span>
                    <span>{label}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Samples Grid */}
        <section className="py-14 md:py-24" style={{ background: '#FAFAFA' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-3" style={{ color: '#1A1F2E' }}>Latest Sample Issues</h2>
              <p className="text-lg text-neutral-gray-dark max-w-2xl mx-auto">Click any sample to read the full issue. New samples added weekly! 📅</p>
            </motion.div>

            <motion.div variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {filteredSamples.map((sample) => (
                <motion.div key={sample.id} variants={staggerItem}>
                  <SampleCard sample={sample} onView={() => setSelectedPDF(sample)} />
                </motion.div>
              ))}
            </motion.div>

            {filteredSamples.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                <div className="text-8xl mb-4">📭</div>
                <h3 className="text-2xl font-display font-bold mb-2" style={{ color: '#1A1F2E' }}>No samples yet!</h3>
                <p className="text-neutral-gray-dark">Check back soon for new sample issues.</p>
              </motion.div>
            )}
          </div>
        </section>

        {/* How to Use */}
        <section className="py-14 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-3" style={{ color: '#1A1F2E' }}>How to Use These Samples</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {howToUse.map((step, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: index * 0.1 }} whileHover={{ y: -6 }}
                  className="rounded-2xl p-7 text-center"
                  style={{ background: `${step.color}08`, border: `1px solid ${step.color}18` }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-display font-bold text-xl mx-auto mb-4"
                    style={{ background: step.color }}>{index + 1}</div>
                  <div className="text-5xl mb-4">{step.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-3" style={{ color: step.color }}>{step.title}</h3>
                  <p className="text-sm text-neutral-gray-dark leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-14 md:py-24" style={{ background: 'linear-gradient(160deg, #E8F5E9, #EFF6FF)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-3" style={{ color: '#1A1F2E' }}>Why Subscribe?</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {benefits.map((b, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }} whileHover={{ y: -8, scale: 1.02 }}
                  className="rounded-2xl p-6 text-center bg-white"
                  style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: `1px solid ${b.color}18` }}>
                  <div className="text-6xl mb-4">{b.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-2" style={{ color: b.color }}>{b.title}</h3>
                  <p className="text-sm text-neutral-gray-dark">{b.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20" style={{ background: 'linear-gradient(135deg, #1E7C24, #145218)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
              <div className="text-7xl mb-5">🎉</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">Love What You See?</h2>
              <p className="text-xl text-white/85 mb-8">Subscribe now and get fresh, exciting content delivered every week!</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a href="https://wa.me/919618377750" target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl text-white"
                  style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.4)' }}>
                  <FaWhatsapp className="text-2xl" /> Subscribe via WhatsApp 🚀
                </motion.a>
                <motion.a href="/contact" whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl"
                  style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.3)' }}>
                  Contact Us 💬
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {selectedPDF && <PDFViewer sample={selectedPDF} onClose={() => setSelectedPDF(null)} />}
      </div>
    </PageTransition>
  );
};

export default Samples;